import cn from 'classnames'
import {FC} from 'react'

import {IncludedPriceBenefits} from '@/components/common/benefits/IncludedPriceBenefits'
import {Text, TextVariant} from '@/components/common/content/Text'
import {FormSummaryCountryDetail} from '@/components/common/form/summary/common/FormSummaryCountryDetail'
import {FormSummaryItemBreakdownRow} from '@/components/common/form/summary/common/layout/FormSummaryItemBreakdownRow'
import {FormSummarySection} from '@/components/common/form/summary/common/layout/FormSummarySection'
import {FormSummary} from '@/components/common/form/summary/FormSummary'
import {FormSummaryPriceWrapper} from '@/components/common/form/summary/price/FormSummaryPriceWrapper'
import {usePreferences} from '@/components/context/Preferences'
import {getFormSummaryExtraClassesTitle} from '@/content/formSummary'
import {getPricingContent} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {ICON_SIZE_MEDIUM} from '@/theme/icons'
import {theme} from '@/theme/theme'
import {
  calculateRenewalExtraClassesFee,
  calculateRenewalJurisdictionsPrice,
  calculateTmRenewalJurisdictionPrice,
} from '@/utils/pricing/renewal/calculations'
import {USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

import {PricingEstimationCardProps} from '../calculator/PricingCalculatorTable'
import {PricingClassSelect} from '../calculator/PricingClassSelect'
import {PricingTrademarksSelect} from '../calculator/PricingTrademarksSelect'

export const PricingTrademarkRenewalEstimationCard: FC<PricingEstimationCardProps> = ({
  selectedJurisdictions,
  classesByJurisdiction,
  trademarksByJurisdiction,
  setClassesByJurisdiction,
  setTrademarksByJurisdiction,
}) => {
  const {t} = useTranslation()
  const {currency} = usePreferences()
  const {
    calculator: {title, description, vatNotIncludedNote, totalPriceTitle, countriesTitle},
    trademarkRenewal: {benefitsList},
  } = getPricingContent()

  const price = calculateRenewalJurisdictionsPrice(
    selectedJurisdictions,
    currency,
    classesByJurisdiction,
    trademarksByJurisdiction,
  )
  const estimatedPrice = price.total.vatBase
  const hasSelectedJurisdictions = selectedJurisdictions.length > 0
  const hasMultipleJurisdictions = selectedJurisdictions.length > 1

  return (
    <>
      <FormSummary
        title={t(title)}
        totalPrice={estimatedPrice}
        currency={currency}
        hasOverlay={false}
      >
        <div className="description">
          <IncludedPriceBenefits
            description={description}
            benefitsList={benefitsList}
            color={theme.palette.text.light}
            disclaimer={null}
          />
        </div>
        {hasSelectedJurisdictions && (
          <FormSummarySection label={t(countriesTitle)}>
            {price.jurisdictions.map(({jurisdiction, total}, i) => {
              const numOfClasses = classesByJurisdiction[jurisdiction] ?? 1
              const numOfTrademarks = trademarksByJurisdiction[jurisdiction] ?? 1
              const numOfExtraClasses = numOfClasses - 1
              const baseJurisdictionPrice = calculateTmRenewalJurisdictionPrice(
                jurisdiction,
                currency,
                1,
                numOfTrademarks,
              )
              const extraClassesFee = calculateRenewalExtraClassesFee(
                jurisdiction,
                currency,
                numOfClasses,
                numOfTrademarks,
              )
              const numOfExtraClassesTitle = getFormSummaryExtraClassesTitle(numOfExtraClasses, t)

              return (
                <FormSummaryCountryDetail
                  key={jurisdiction}
                  jurisdiction={jurisdiction}
                  totalPrice={total.vatBase}
                  serviceFee={baseJurisdictionPrice.legalFee.total.vatBase}
                  administrativeFee={baseJurisdictionPrice.registrationFee.total.vatBase}
                  trackedEvent={{
                    name: 'Price breakdown expanded',
                    parameters: {
                      country: jurisdiction,
                      tab: 'Trademark renewal',
                      page: 'Pricing page',
                    },
                  }}
                  currency={currency}
                  isPriceTooltipVisible={false}
                  isExpanded
                  textColor={theme.palette.text.default}
                >
                  {numOfExtraClasses > 0 && (
                    <FormSummaryItemBreakdownRow
                      title={numOfExtraClassesTitle}
                      price={extraClassesFee}
                      currency={currency}
                    />
                  )}
                  <div
                    className={cn(
                      'wrapper',
                      hasMultipleJurisdictions && i < price.jurisdictions.length - 1 && 'divider',
                    )}
                  >
                    <div className="input-wrapper">
                      <PricingTrademarksSelect
                        minSelectedTrademarks={1}
                        selectedTrademarks={trademarksByJurisdiction[jurisdiction] ?? 1}
                        setSelectedTrademarks={(trademarks) =>
                          setTrademarksByJurisdiction((prev) => ({
                            ...prev,
                            [jurisdiction]: trademarks,
                          }))
                        }
                        trackedEvent={{
                          name: 'Change number of trademarks (Pricing page)',
                          parameters: {
                            country: jurisdiction,
                            tab: 'Trademark renewal',
                            page: 'Pricing page',
                          },
                        }}
                      />
                    </div>
                    <div className="input-wrapper">
                      <PricingClassSelect
                        minSelectedClasses={1}
                        selectedClasses={numOfClasses}
                        setSelectedClasses={(classes) =>
                          setClassesByJurisdiction((prev) => ({...prev, [jurisdiction]: classes}))
                        }
                        title={{color: theme.palette.text.light}}
                        trackedEvent={{
                          name: 'Change number of classes (Pricing page)',
                          parameters: {
                            country: jurisdiction,
                            tab: 'Trademark renewal',
                            page: 'Pricing page',
                          },
                        }}
                        hasLearnMore={false}
                      />
                    </div>
                  </div>
                </FormSummaryCountryDetail>
              )
            })}
          </FormSummarySection>
        )}
        <FormSummaryPriceWrapper
          priceTitle={t(totalPriceTitle)}
          estimatedPrice={estimatedPrice}
          hasDiscountInput={false}
        />
        <div className="note">
          <Text variant={TextVariant.caption} element="p">
            {t(vatNotIncludedNote)}
          </Text>
        </div>
      </FormSummary>

      <style jsx>{`
        .note {
          margin-top: ${theme.spacing[2]};
          margin-bottom: ${theme.spacing[4]};
        }
        .description {
          margin-bottom: ${theme.spacing[4]};
        }
        .wrapper {
          margin-top: ${theme.spacing[2.5]};
        }
        .divider {
          padding-bottom: ${theme.spacing[3]};
          margin-bottom: ${theme.spacing[2]};
          border-bottom: 1px solid ${theme.palette.gray[200]};
        }
        .input-wrapper {
          padding: ${theme.spacing[2]};
          padding-left: ${theme.spacing[2.5]};
          margin-left: calc(${ICON_SIZE_MEDIUM} + ${theme.spacing[2]});
          background-color: ${theme.palette.gray[100]};
          border-radius: 6px;
        }
        .input-wrapper + .input-wrapper {
          margin-top: ${theme.spacing[2]};
        }
        @media ${USER_TABLET_SCREENSIZE} {
          .note {
            margin-bottom: 0;
          }
          .description {
            display: none;
          }
        }
      `}</style>
    </>
  )
}
